// The Player maps tab: maps players built and published, sorted by rating, plays or date, with
// favourites. A map opens to show its details, your rating and its hiscores. Also opens a map by code.

import { online, type MapSort, type PlayerMap } from '../online/client';
import { ago, fillBoard } from './boardList';

const $ = (id: string) => document.getElementById(id)!;

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls = '', text = '') => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text) e.textContent = text;
  return e;
};

/** Link that opens a published map (the game reads ?map= on start). */
export const mapLink = (code: string) => `${location.origin}${location.pathname}?map=${code}`;

export class PlayerMaps {
  /** Play a published map by its code. */
  onPlay: (code: string) => void = () => {};
  /** Text that isn't a map code (a level shared as text): import it. */
  onImportText: (text: string) => void = () => {};
  /** The player's current rules: hiscore key and label. */
  rules: () => { key: string; label: string } = () => ({ key: 'aa1337-auto', label: '' });

  private sort: MapSort = 'top';
  private page = 0;
  private seq = 0;
  private started = false;
  private searchTimer = 0;

  constructor() {
    for (const b of document.querySelectorAll<HTMLButtonElement>('#pmSort [data-sort]'))
      b.addEventListener('click', () => {
        this.sort = b.dataset.sort as MapSort;
        for (const o of document.querySelectorAll('#pmSort [data-sort]')) o.classList.toggle('on', o === b);
        this.load(true);
      });
    $('pmSearch').addEventListener('input', () => {
      clearTimeout(this.searchTimer);
      this.searchTimer = window.setTimeout(() => this.load(true), 350);
    });
    $('pmMore').addEventListener('click', () => this.load(false));
    const open = () => {
      const input = $('codeInput') as HTMLInputElement;
      const v = input.value.trim();
      if (!v) return;
      input.value = '';
      const code = v
        .replace(/^.*[?&]map=/, '')
        .replace(/[\s-]/g, '')
        .toUpperCase()
        .replace(/^BH(?=.{6}$)/, '');
      if (/^[2-9A-Z]{6}$/.test(code)) this.onPlay(code);
      else this.onImportText(v);
    };
    $('codeOpen').addEventListener('click', open);
    $('codeInput').addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter') open();
    });
    online.onChange(() => {
      if (this.started) this.load(true);
    });
  }

  /** First time the tab opens. */
  start() {
    if (this.started) return;
    this.started = true;
    this.load(true);
  }

  status(msg: string) {
    $('pmStatus').textContent = msg;
  }

  load(reset: boolean) {
    const seq = ++this.seq;
    if (reset) this.page = 0;
    const sort = this.sort;
    const q = ($('pmSearch') as HTMLInputElement).value;
    if ((sort === 'favorites' || sort === 'mine') && !online.me) {
      $('pmList').textContent = '';
      $('pmMore').hidden = true;
      this.status('Pick a hiscore name (Settings) to keep favourites and publish maps.');
      return;
    }
    this.status('Loading…');
    online
      .maps(sort, q, this.page)
      .then(({ maps, more }) => {
        if (seq !== this.seq) return;
        if (reset) $('pmList').textContent = '';
        for (const m of maps) $('pmList').append(this.row(m));
        this.page++;
        $('pmMore').hidden = !more;
        const empty = reset && !maps.length;
        this.status(
          !empty
            ? ''
            : sort === 'favorites'
              ? 'No favourites yet. Open a map and click ☆ Favourite to keep it here.'
              : sort === 'mine'
                ? "You haven't published a map yet. Finish one of your levels in the Map Builder, then publish it."
                : q
                  ? 'No maps match.'
                  : 'No maps yet. Build one in the Map Builder and publish it!',
        );
      })
      .catch((e: Error) => {
        if (seq === this.seq) this.status(e.message);
      });
  }

  private row(m: PlayerMap): HTMLLIElement {
    const li = el('li', 'pm');
    const head = el('button', 'pmRow');
    head.type = 'button';
    head.setAttribute('aria-expanded', 'false');
    const title = el('span', 'pmTitle');
    title.append(el('b', '', m.title), el('small', '', `by ${m.author}`));
    const stats = el('span', 'pmStats');
    const rating = el('span', 'rating');
    const paintRating = () => {
      rating.textContent = m.rating === null ? 'not rated' : `★ ${m.rating.toFixed(1)}`;
      rating.title = `${m.ratings} rating${m.ratings === 1 ? '' : 's'}`;
    };
    paintRating();
    const favMark = el('span', 'favMark', m.favorite ? '★' : '');
    stats.append(rating, el('span', 'plays', `${m.downloads} played`), favMark);
    head.append(title, stats, el('span', 'chev', '›'));

    const detail = el('div', 'pmDetail');
    detail.hidden = true;
    let filled = false;
    head.addEventListener('click', () => {
      detail.hidden = !detail.hidden;
      li.classList.toggle('open', !detail.hidden);
      head.setAttribute('aria-expanded', String(!detail.hidden));
      if (!detail.hidden && !filled) {
        filled = true;
        this.fillDetail(detail, m, () => {
          paintRating();
          favMark.textContent = m.favorite ? '★' : '';
        });
      }
    });
    li.append(head, detail);
    return li;
  }

  private fillDetail(detail: HTMLElement, m: PlayerMap, changed: () => void) {
    const btn = (label: string, cls: string, fn: (b: HTMLButtonElement) => void) => {
      const b = el('button', cls, label);
      b.type = 'button';
      b.addEventListener('click', () => fn(b));
      return b;
    };
    const flash = (b: HTMLButtonElement, text: string) => {
      const was = b.textContent;
      b.textContent = text;
      setTimeout(() => (b.textContent = was), 1500);
    };

    const actions = el('div', 'pmActions');
    const fav = btn(m.favorite ? '★ Favourite' : '☆ Favourite', 'secondary fav', async (b) => {
      if (!online.me) return this.status('Pick a hiscore name (Settings) to keep favourites.');
      try {
        m.favorite = (await online.favorite(m.code, !m.favorite)).favorite;
        b.textContent = m.favorite ? '★ Favourite' : '☆ Favourite';
        b.classList.toggle('on', m.favorite);
        changed();
      } catch (e) {
        this.status((e as Error).message);
      }
    });
    fav.classList.toggle('on', m.favorite);
    actions.append(
      btn('▶ Play', 'primaryBtn', () => this.onPlay(m.code)),
      fav,
      btn('Copy link', 'secondary', (b) => {
        void navigator.clipboard?.writeText(mapLink(m.code)).then(() => flash(b, 'Link copied'));
      }),
      btn('Copy code', 'secondary', (b) => {
        void navigator.clipboard?.writeText(m.code).then(() => flash(b, 'Copied'));
      }),
    );
    if (m.mine)
      actions.append(
        btn('Delete', 'secondary danger', async () => {
          if (!confirm(`Delete "${m.title}" for everyone? Its hiscores go too.`)) return;
          try {
            await online.deleteMap(m.code);
            detail.parentElement?.remove();
          } catch (e) {
            this.status((e as Error).message);
          }
        }),
      );

    // Your rating.
    const rate = el('div', 'pmRate');
    if (m.mine) rate.append(el('span', 'hint', 'Your map. Others rate it after playing.'));
    else {
      rate.append(el('span', 'lbl', 'Your rating'));
      const stars = el('span', 'rate');
      const paint = () =>
        [...stars.children].forEach((c, i) => c.classList.toggle('on', i < (m.myRating ?? 0)));
      for (let s = 1; s <= 5; s++) {
        const b = btn('★', 'star', async () => {
          if (!online.me) return this.status('Pick a hiscore name (Settings) to rate maps.');
          try {
            const r = await online.rate(m.code, m.myRating === s ? 0 : s);
            m.myRating = r.myRating;
            m.rating = r.map.rating;
            m.ratings = r.map.ratings;
            paint();
            changed();
          } catch (e) {
            this.status((e as Error).message);
          }
        });
        b.setAttribute('aria-label', `${s} star${s === 1 ? '' : 's'}`);
        stars.append(b);
      }
      paint();
      rate.append(stars, el('span', 'hint', 'click again to remove'));
    }

    const info = el(
      'div',
      'pmInfo',
      `Code ${m.code} · ${m.pieces} pieces · published ${ago(m.created)} · ` +
        (m.rating === null ? 'no ratings yet' : `★ ${m.rating.toFixed(1)} from ${m.ratings}`),
    );

    // Its hiscores, for your rules.
    const rules = this.rules();
    const board = el('div', 'pmBoard');
    const h = el('h4', '', 'Top times');
    h.append(el('span', '', rules.label));
    const list = el('ol', 'board');
    const note = el('p', 'hint', 'Loading hiscores…');
    board.append(h, list, note);
    online
      .board('p:' + m.code, rules.key, 5)
      .then((b) => {
        fillBoard(list, b, online.me?.name.toLowerCase(), online.me?.name);
        note.textContent = b.top.length
          ? `${b.total} player${b.total === 1 ? '' : 's'} · hold Tab while playing to see them`
          : 'No times with these settings yet. Be the first!';
      })
      .catch((e: Error) => (note.textContent = e.message));

    detail.append(actions, rate, info, board);
  }
}
